"use client";

import DynamicIcon from "@/components/atoms/DynamicIcon";
import { ctaClick } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import { isCmsShowFlagOn } from "@/utils/variantHelpers";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { LEAD_DROP_VARIANTS } from "../../../constants";

/**
 * Banner 1062 — KUK programme referral band.
 *
 * Port of kuk-campus-portal `components/Referral/ReferalPostLogin.tsx`: the same
 * artwork-backed band Banner 1059 carries on the home page, at a larger type scale,
 * followed by a three-column strip of USP tiles — each an image in a circle beside a
 * line of copy, on its own fill.
 *
 * WHY IT IS NOT BANNER 1059. 1059 ports `ReferralPreLogin`, which has no USP strip
 * and sets its headline at `text-2xl`/`tab:text-[32px]`; this one runs
 * `text-[32px]`/`tab:text-5xl` and the strip is the point of the section. Rather than
 * bolt an "also show tiles" flag onto 1059 and have two layouts fight over one set of
 * type scales, the two stay separate variants, as they are two components upstream.
 *
 * THE TILES are `metadata` rows, so their number is not fixed at three; the grid is
 * `grid-cols-3` at every width, which is v1's own markup.
 *
 * THE HIGHLIGHT — v1 passes the headline through a helper that turns `{` and `}` into
 * a `<span>` with a highlight class, so "earn up to {50%} of your course fee" paints a
 * chip behind "50%". Reproduced with the same brace syntax, with the chip colour as a
 * CMS field rather than a fixed class. The headline is escaped before the braces are
 * expanded, so stored markup cannot inject anything.
 *
 * BREAKPOINTS — the v1 source is built on Tailwind's DEFAULT scale, where `md:` is
 * 768px and `lg:` is 1024px. This app remaps `md:` to 1230px and exposes 768px as
 * `tab:` (see globals.css `--breakpoint-*`), so the reference's `md:` is ported as
 * `tab:` and its `lg:` as this app's `sm:` (1080px).
 *
 * NOT CARRIED OVER — v1's hardcoded `/launchpad` href on the CTA and its rocket icon
 * from `public/icons`. Both are CMS fields here (`apply_now_link`, `icon`), because
 * this section is placed on sixteen programme pages and the destination is the kind
 * of thing that moves.
 */

interface UspTile {
  id?: string | number;
  meta_image?: string;
  meta_alt_image?: string;
  meta_title?: string;
  /** Tile fill — v1 alternates two purples across the three tiles. */
  meta_background_color?: string;
  meta_text_color?: string;
  meta_priority?: number;
  meta_status?: boolean;
}

interface Banner1062Data {
  /** USP tiles under the band. */
  metadata?: UspTile[];
  /** Artwork from 1080px up. */
  background_image?: string;
  /** Artwork below 1080px; falls back to the desktop artwork. */
  background_image_mobile?: string;
  image_alt?: string;
  /** Headline. `{...}` wraps a highlighted chip. */
  title?: string;
  title_color?: string;
  /** The chip behind a `{...}` span. */
  title_highlight_color?: string;
  description?: string;
  description_color?: string;
  apply_now_show?: boolean;
  apply_now_text?: string;
  apply_now_link?: string;
  apply_now_is_cta?: boolean;
  apply_now_color?: string;
  /** CTA pill gradient — colour 1, colour 2, angle. */
  gradient_color_1?: string;
  gradient_color_2?: string;
  gradient_color_3?: string;
  /** Icon-map key or an uploaded image URL, shown inside the pill. */
  icon?: string;
  status?: boolean;
}

const ESCAPE: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

/**
 * v1's `textStyle`: `{` opens a highlighted span, `}` closes it. The headline is
 * escaped first, so only the braces this widget understands become markup.
 */
function highlightBraces(title: string, color: string): string {
  if (!title) return "";
  return title
    .replace(/[&<>"']/g, (c) => ESCAPE[c])
    .replace(
      /\{/g,
      `<span class="whitespace-nowrap" style="background-color:${color}">`,
    )
    .replace(/\}/g, "</span>");
}

function normalizeHref(href: string): string {
  const h = href.trim();
  if (!h) return "#";
  if (
    h.startsWith("http://") ||
    h.startsWith("https://") ||
    h.startsWith("/") ||
    h.startsWith("#") ||
    h.startsWith("mailto:") ||
    h.startsWith("tel:")
  ) {
    return h;
  }
  return `/${h.replace(/^\//, "")}`;
}

const Banner1062 = (props: { data?: Banner1062Data }) => {
  const { data: raw } = props;
  const sectionData = ((raw as any)?.data ?? raw) as Banner1062Data | undefined;
  const dispatch = useDispatch();

  const {
    background_image = "",
    background_image_mobile = "",
    image_alt = "",
    title = "",
    title_color = "#FFFFFF",
    title_highlight_color = "#E26C24",
    description = "",
    description_color = "#FFFFFF",
    apply_now_text = "Start referring Now!",
    apply_now_link = "",
    apply_now_color = "#FFFFFF",
    gradient_color_1 = "#B0325E",
    gradient_color_2 = "#FAA681",
    gradient_color_3 = "47deg",
    icon = "",
  } = sectionData || {};

  const applyNowShow = isCmsShowFlagOn(sectionData?.apply_now_show, true);
  const applyNowIsCta = isCmsShowFlagOn(sectionData?.apply_now_is_cta, true);
  const status = isCmsShowFlagOn(sectionData?.status, true);

  const headline = useMemo(
    () => highlightBraces(title, title_highlight_color),
    [title, title_highlight_color],
  );

  const tiles = useMemo(() => {
    const rows = sectionData?.metadata;
    if (!Array.isArray(rows)) return [] as UspTile[];
    return rows
      .filter((r) => r?.meta_status !== false && (r?.meta_title || r?.meta_image))
      .sort(
        (a, b) => (Number(a?.meta_priority) || 0) - (Number(b?.meta_priority) || 0),
      );
  }, [sectionData?.metadata]);

  const handleApplyNowClick = useCallback(() => {
    ctaClick({
      section: "banner_1062",
      component: "referral_cta",
      label: apply_now_text?.trim() || "start_referring",
      step: "1",
      entity: "banner_1062",
      parentEntity: "banner_1062",
    });
    dispatch(handleOpenLeaddrop({ variant: LEAD_DROP_VARIANTS.REGISTER }));
  }, [apply_now_text, dispatch]);

  if (!status) return null;

  const desktopArt = background_image || background_image_mobile;
  const mobileArt = background_image_mobile || background_image;

  const ctaClassName = cn(
    "mt-5 flex items-center gap-2 self-start rounded-3xl px-4 py-1",
    "text-base font-semibold tab:py-2.5",
  );
  const ctaStyle = {
    backgroundImage: `linear-gradient(${gradient_color_3}, ${gradient_color_1} 3.7%, ${gradient_color_2} 106.2%)`,
    color: apply_now_color,
  };

  const ctaInner = (
    <>
      {icon ? (
        <DynamicIcon
          iconName={icon}
          width="34"
          height="34"
          color={apply_now_color}
        />
      ) : null}
      <span>{apply_now_text}</span>
    </>
  );

  return (
    <section className="flex w-full flex-col">
      <div
        className={cn(
          "relative w-full overflow-hidden px-6 pb-[230px] pt-14",
          "tab:px-12 tab:pb-16 tab:pt-20",
        )}
      >
        <div className="absolute inset-0 z-0 size-full">
          {mobileArt ? (
            <Image
              src={mobileArt}
              alt={image_alt || ""}
              fill
              loading="lazy"
              sizes="100vw"
              className="block object-cover tab:hidden"
            />
          ) : null}
          {desktopArt ? (
            <Image
              src={desktopArt}
              alt={image_alt || ""}
              fill
              loading="lazy"
              sizes="100vw"
              className="hidden object-cover tab:block"
            />
          ) : null}
        </div>

        <div className="relative z-10 mx-auto w-full max-w-[1120px]">
          <div className="flex max-w-[264px] flex-col tab:max-w-[400px]">
            {title ? (
              <p
                className={cn(
                  "w-[208px] text-[32px] font-bold leading-[44px]",
                  "tab:w-full tab:text-5xl tab:leading-[54px]",
                )}
                style={{ color: title_color }}
                dangerouslySetInnerHTML={{ __html: headline }}
              />
            ) : null}
            {description ? (
              <p
                className="mt-4 text-xs font-medium tab:text-base"
                style={{ color: description_color }}
              >
                {description}
              </p>
            ) : null}
            {applyNowShow && apply_now_text ? (
              applyNowIsCta || !apply_now_link ? (
                <button
                  type="button"
                  onClick={handleApplyNowClick}
                  className={ctaClassName}
                  style={ctaStyle}
                >
                  {ctaInner}
                </button>
              ) : (
                <Link
                  href={normalizeHref(apply_now_link)}
                  className={ctaClassName}
                  style={ctaStyle}
                >
                  {ctaInner}
                </Link>
              )
            ) : null}
          </div>
        </div>
      </div>

      {tiles.length > 0 ? (
        <div className="grid w-full grid-cols-3 items-stretch">
          {tiles.map((tile, i) => (
            <div
              key={tile.id ?? i}
              className={cn(
                "flex flex-col items-center justify-center gap-4 px-4 py-6 text-center",
                "sm:flex-row sm:justify-start sm:px-12 sm:text-left",
                i === 0 && "sm:pl-[92px]",
              )}
              style={{ backgroundColor: tile.meta_background_color || "#6C276A" }}
            >
              <div className="size-10 shrink-0 overflow-hidden rounded-full tab:size-[70px]">
                {tile.meta_image ? (
                  <Image
                    src={tile.meta_image}
                    alt={tile.meta_alt_image || tile.meta_title || ""}
                    width={70}
                    height={70}
                    loading="lazy"
                    className="size-full rounded-full object-cover"
                  />
                ) : null}
              </div>
              {tile.meta_title ? (
                <p
                  className="w-full text-xs font-bold tab:text-xl sm:max-w-[205px]"
                  style={{ color: tile.meta_text_color || "#FFFFFF" }}
                >
                  {tile.meta_title}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default Banner1062;
